import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    Button,
    Stack,
    alpha,
    useTheme,
    Divider,
    Fade,
    IconButton,
} from '@mui/material';
import {
    Download,
    X,
    CheckCircle2,
    Smartphone,
    Zap,
    WifiOff,
    Share2,
    PlusSquare,
} from 'lucide-react';

const FEATURES = [
    { icon: Zap, label: 'Acesso instantâneo sem abrir o navegador' },
    { icon: WifiOff, label: 'Funciona mesmo com conexão instável' },
    { icon: Smartphone, label: 'Experiência nativa no celular e desktop' },
];

function IOSInstructions() {
    const theme = useTheme();
    const step = (icon, text) => (
        <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>
            <Typography variant="body2" color="text.secondary">
                {text}
            </Typography>
        </Stack>
    );

    return (
        <Stack spacing={1.5} sx={{ mt: 1 }}>
            {step(
                <Share2 size={16} color={theme.palette.primary.main} />,
                'Toque no botão Compartilhar no Safari'
            )}
            {step(
                <PlusSquare size={16} color={theme.palette.primary.main} />,
                'Selecione "Adicionar à Tela de Início"'
            )}
            {step(
                <CheckCircle2 size={16} color={theme.palette.success.main} />,
                'Confirme tocando em "Adicionar"'
            )}
        </Stack>
    );
}

export default function PWAInstallModal({
    open,
    isIOSDevice,
    onInstall,
    onDismiss,
}) {
    const theme = useTheme();
    const [installing, setInstalling] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleInstall = async () => {
        setInstalling(true);
        const accepted = await onInstall();
        setInstalling(false);
        if (accepted) {
            setSuccess(true);
            // `false`: apenas FECHA. Gravar a dispensa permanente aqui era o bug que
            // impedia o convite de voltar depois de DESINSTALAR o app — a flag fica no
            // localStorage, que sobrevive a desinstalacao. Quem ja instalou nao e
            // incomodado porque o provider ve `display-mode: standalone`, condicao que
            // se corrige sozinha quando o app sai do aparelho.
            setTimeout(() => onDismiss(false), 2200);
        }
    };

    const accentColor = theme.palette.primary.main;

    return (
        <Dialog
            open={open}
            onClose={() => onDismiss(false)}
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 300 }}
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    maxWidth: 400,
                    width: '100%',
                    mx: 2,
                    background: theme.palette.background.paper,
                    border: `1px solid ${alpha(accentColor, 0.15)}`,
                    overflow: 'hidden',
                },
            }}
        >
            {/* Top gradient bar */}
            <Box
                sx={{
                    height: 4,
                    background: `linear-gradient(90deg, ${accentColor}, ${alpha(accentColor, 0.3)})`,
                }}
            />

            {/* Close button */}
            <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                <IconButton
                    size="small"
                    onClick={() => onDismiss(false)}
                    sx={{ color: 'text.secondary' }}
                >
                    <X size={18} />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 3, pt: 2.5 }}>
                {success ? (
                    <Stack alignItems="center" spacing={2} py={2}>
                        <Box
                            sx={{
                                width: 72,
                                height: 72,
                                borderRadius: '50%',
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                animation: 'pop 0.4s ease-out',
                                '@keyframes pop': {
                                    '0%': { transform: 'scale(0.5)', opacity: 0 },
                                    '70%': { transform: 'scale(1.15)' },
                                    '100%': { transform: 'scale(1)', opacity: 1 },
                                },
                            }}
                        >
                            <CheckCircle2
                                size={36}
                                color={theme.palette.success.main}
                            />
                        </Box>
                        <Typography variant="h6" fontWeight={800} textAlign="center">
                            App instalado com sucesso!
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            textAlign="center"
                        >
                            O CRM AUFTEK foi adicionado à sua tela de início.
                        </Typography>
                    </Stack>
                ) : (
                    <>
                        {/* App icon + title */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            mb={2.5}
                        >
                            <Box
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: '14px',
                                    background: `linear-gradient(135deg, ${accentColor} 0%, ${theme.palette.primary.dark} 100%)`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    boxShadow: `0 6px 20px ${alpha(accentColor, 0.35)}`,
                                }}
                            >
                                <img
                                    src="/logo192.png"
                                    alt="CRM AUFTEK"
                                    style={{
                                        width: 38,
                                        height: 38,
                                        objectFit: 'contain',
                                    }}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </Box>
                            <Box>
                                <Typography
                                    variant="h6"
                                    fontWeight={800}
                                    lineHeight={1.2}
                                >
                                    Instale o CRM AUFTEK
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight={600}
                                >
                                    Acesso rápido direto na sua tela
                                </Typography>
                            </Box>
                        </Stack>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={2}
                        >
                            {isIOSDevice
                                ? 'Adicione o CRM à sua tela de início para acesso instantâneo, como um app nativo.'
                                : 'Instale o app para acessar o CRM sem precisar abrir o navegador, com performance e experiência superiores.'}
                        </Typography>

                        {/* Features */}
                        <Stack spacing={1} mb={2.5}>
                            {FEATURES.map(({ icon: Icon, label }) => (
                                <Stack
                                    key={label}
                                    direction="row"
                                    alignItems="center"
                                    spacing={1.5}
                                >
                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: '7px',
                                            bgcolor: alpha(accentColor, 0.1),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Icon size={14} color={accentColor} />
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        fontSize="0.8rem"
                                    >
                                        {label}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>

                        {isIOSDevice ? (
                            <>
                                <Divider sx={{ mb: 2, opacity: 0.1 }} />
                                <Typography
                                    variant="caption"
                                    fontWeight={700}
                                    color="text.primary"
                                    display="block"
                                    mb={1}
                                >
                                    Como instalar no iOS:
                                </Typography>
                                <IOSInstructions />
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => onDismiss(false)}
                                    sx={{
                                        mt: 2.5,
                                        borderRadius: '10px',
                                        textTransform: 'none',
                                        fontWeight: 700,
                                    }}
                                >
                                    Entendido
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    startIcon={
                                        installing ? null : <Download size={18} />
                                    }
                                    onClick={handleInstall}
                                    disabled={installing}
                                    sx={{
                                        borderRadius: '10px',
                                        textTransform: 'none',
                                        fontWeight: 800,
                                        fontSize: '0.95rem',
                                        py: 1.25,
                                        background: `linear-gradient(135deg, ${accentColor} 0%, ${theme.palette.primary.dark} 100%)`,
                                        boxShadow: `0 4px 14px ${alpha(accentColor, 0.4)}`,
                                        '&:hover': {
                                            boxShadow: `0 6px 18px ${alpha(accentColor, 0.5)}`,
                                        },
                                    }}
                                >
                                    {installing ? 'Instalando…' : 'Instalar Agora'}
                                </Button>
                                <Button
                                    fullWidth
                                    variant="text"
                                    onClick={() => onDismiss(true)}
                                    sx={{
                                        mt: 1,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: '0.78rem',
                                        color: 'text.disabled',
                                        '&:hover': { color: 'text.secondary' },
                                    }}
                                >
                                    Não mostrar novamente
                                </Button>
                            </>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
